import cx from 'classnames';
import i18next from 'i18next';
import { first, isEmpty, isString } from 'lodash';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import { Button, QrCode } from 'components';
import { COPY_ICON_PATH, IMAGES_PATH } from 'constants/images.constants';
import { PAYMENT_TYPES_WITH_PAYMENT_LINK_QR } from 'constants/payment-type.constants';
import { PaymentType } from 'enums';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites, RequisitesCard, RequisitesSBP, TradeMethod } from 'types';
import { orderRequisitesUtils, paymentLinkAppsUtils } from 'utils';

type Field = {
  name: string;
  type: string;
  label: string;
  value: string;
};

type Props = {
  tradeMethod: TradeMethod;
  requisites?: Requisites;
  requisitesCountry: string;
  deeplinksExist?: boolean;
};

export const PaymentFields: React.FC<Props> = ({
  tradeMethod,
  requisites,
  requisitesCountry,
  deeplinksExist,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const { nspkUrl, qrCodeUrl, requisitesBankName } = useOrderContext();

  const isSberPay = useMemo(
    () => tradeMethod.paymentType === PaymentType.SberPay,
    [tradeMethod.paymentType],
  );

  const isSIM = useMemo(
    () => tradeMethod.paymentType === PaymentType.SIM,
    [tradeMethod.paymentType],
  );

  const isNSPK = useMemo(
    () => tradeMethod.paymentType === PaymentType.NSPK,
    [tradeMethod.paymentType],
  );

  const isAccountNumberWithQr = useMemo(
    () =>
      requisites?.qrImageUrl &&
      tradeMethod.paymentType === PaymentType.AccountNumber,
    [tradeMethod.paymentType, requisites?.qrImageUrl],
  );

  // SberQR Vietnam: prefer the amount-filled payment string, fall back to the raw one
  const paymentStringQrData = useMemo(
    () => requisites?.paymentStringVietnamSberQR || requisites?.paymentString,
    [requisites?.paymentStringVietnamSberQR, requisites?.paymentString],
  );

  const isCopyAvailable = useMemo(
    () => !!navigator?.clipboard?.writeText && !isAccountNumberWithQr,
    [isAccountNumberWithQr],
  );

  const shouldShowQrCode = useMemo(
    () =>
      !deeplinksExist &&
      (isAccountNumberWithQr ||
        isNSPK ||
        (PAYMENT_TYPES_WITH_PAYMENT_LINK_QR.includes(tradeMethod.paymentType) &&
          requisites?.paymentLink)),
    [
      deeplinksExist,
      isAccountNumberWithQr,
      isNSPK,
      tradeMethod.paymentType,
      requisites?.paymentLink,
    ],
  );

  const bank = useMemo(() => requisites?.bank || '', [requisites?.bank]);

  const countryCode = useMemo(
    () => requisites?.countryCode || '',
    [requisites?.countryCode],
  );

  const cardInfo = useMemo(
    () => (requisites as RequisitesCard)?.cardInfo || '',
    [requisites],
  );

  const phone = useMemo(
    () => (requisites as RequisitesSBP)?.phone || '',
    [requisites],
  );

  const paymentLink = useMemo(
    () => requisites?.paymentLink || '',
    [requisites?.paymentLink],
  );

  const paymentLinkAppLinks = useMemo(
    () => paymentLinkAppsUtils.getPaymentLinkAppLinks(paymentLink),
    [paymentLink],
  );

  const getFirstRequisitesTradeMethodField = useCallback(() => {
    const field = first(tradeMethod?.fields);
    const fieldValue = field && requisites?.[field.name as keyof Requisites];
    return isString(fieldValue) ? fieldValue : '';
  }, [requisites, tradeMethod?.fields]);

  const formattedCardInfo = useMemo(() => {
    if (tradeMethod.paymentType === PaymentType.Card2Card) {
      return cardInfo.replace(/(\d{4})(?=\d)/g, '$1 ');
    }
    return cardInfo;
  }, [cardInfo, tradeMethod.paymentType]);

  const copyToClipboard = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);

  const copyCardInfo = useCallback(() => {
    const requisiteDataToCopy =
      (tradeMethod.paymentType === PaymentType.PhoneNumber ? phone : '') ||
      cardInfo ||
      phone ||
      paymentLink ||
      nspkUrl ||
      getFirstRequisitesTradeMethodField();
    requisiteDataToCopy && copyToClipboard(requisiteDataToCopy);
  }, [
    cardInfo,
    getFirstRequisitesTradeMethodField,
    phone,
    copyToClipboard,
    paymentLink,
    nspkUrl,
    tradeMethod.paymentType,
  ]);

  const renderPaymentLinkAppButtons = useCallback(
    () =>
      paymentLinkAppLinks.length ? (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-4 md:tw-hidden">
          {paymentLinkAppLinks.map((link) => (
            <a
              key={link.code}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-flex tw-h-[56px] tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-px-4 tw-text-center tw-text-base tw-font-semibold tw-text-white tw-no-underline"
              style={{ background: link.gradient }}
            >
              <span
                className="tw-mr-3 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-lg tw-font-bold tw-leading-none"
                style={{
                  background: link.iconBackground,
                  color: link.code === 'bakai' ? '#D7193F' : '#F5C400',
                }}
              >
                {link.iconText}
              </span>
              {t('deeplink.title', { bankName: link.name })}
            </a>
          ))}
        </div>
      ) : null,
    [paymentLinkAppLinks, t],
  );

  const renderField = useCallback(
    (field: Field) => {
      let valueNode: React.ReactNode = field.value;
      if (field.name === 'paymentLink') {
        valueNode = (
          <Fragment>
            <a
              href={field.value}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-break-all tw-text-blue-600 tw-underline hover:tw-text-blue-800 tw-font-semibold tw-transition-colors"
            >
              {orderRequisitesUtils.formatPaymentLink(field.value)}
            </a>
            {isCopyAvailable && (
              <SVG
                src={COPY_ICON_PATH}
                title="copy"
                className="tw-w-3.5 tw-h-3.5 tw-ml-3 tw-inline tw-cursor-pointer"
                onClick={() => copyToClipboard(field.value)}
              />
            )}
          </Fragment>
        );
      }

      if (!field.label && !field.value) {
        return null;
      }

      return (
        <div
          key={field.name}
          className="tw-mb-3 tw-flex tw-w-full tw-items-start tw-gap-2 tw-text-sm tw-leading-snug last:tw-mb-0 md:tw-mr-3 md:tw-block md:tw-text-xl md:tw-leading-normal"
        >
          <span className="tw-min-w-0 tw-flex-1">
            <span className="tw-font-normal">{`${field.label}: `}</span>
            <span className="tw-break-words">{valueNode}</span>
          </span>
        </div>
      );
    },
    [isCopyAvailable, copyToClipboard],
  );

  const renderQrPaymentLinkField = useCallback(
    (field: Field) => {
      let valueNode: React.ReactNode = field.value;
      if (field.name === 'paymentLink') {
        valueNode = (
          <Fragment>
            <a
              href={field.value}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-break-all tw-text-blue-600 tw-underline hover:tw-text-blue-800 tw-font-semibold tw-transition-colors"
            >
              {orderRequisitesUtils.formatPaymentLink(field.value)}
            </a>
            {isCopyAvailable && (
              <SVG
                src={COPY_ICON_PATH}
                title="copy"
                className="tw-w-3.5 tw-h-3.5 tw-ml-3 tw-inline tw-cursor-pointer"
                onClick={() => copyToClipboard(field.value)}
              />
            )}
          </Fragment>
        );
      }

      if (!field.label && !field.value) {
        return null;
      }

      return (
        <div
          key={field.name}
          className="tw-flex tw-w-full tw-items-start tw-gap-2 tw-text-left tw-text-base tw-leading-snug md:tw-justify-center md:tw-text-center md:tw-text-lg"
        >
          <span className="tw-min-w-0">
            <span className="tw-font-normal">{`${field.label}: `}</span>
            <span className="tw-break-words">{valueNode}</span>
          </span>
        </div>
      );
    },
    [isCopyAvailable, copyToClipboard],
  );

  // stf
  const formattedFields: Field[] = useMemo(
    () =>
      [
        ...tradeMethod.fields,
        ...(!isNSPK && bank ? [{ name: 'bank', type: 'bank' }] : []),
        ...(countryCode ? [{ name: 'countryCode', type: 'country' }] : []),
      ]
        .filter((field) => {
          if (field.name === 'paymentLink' && shouldShowQrCode) {
            return false;
          }
          const fieldValue = requisites?.[field.name as keyof Requisites];
          return isString(fieldValue) && fieldValue.trim();
        })
        .map(({ name, type }) => {
          let rawValue =
            requisites?.[name as keyof Omit<Requisites, 'sameBank'>] || '';

          let label = `${t(`${type}.label`, {
            defaultValue: type,
          })}`;

          if (
            tradeMethod.paymentType === PaymentType.Card2Card &&
            name === 'cardInfo'
          ) {
            rawValue = formattedCardInfo;
          }

          if (bank && name === 'bank') {
            rawValue = requisitesBankName || '';
            if (isSIM) {
              label = `${t(`${type}.mobile_operator_label`, {
                defaultValue: type,
              })}`;
            }
          }

          if (countryCode && name === 'countryCode') {
            rawValue = requisitesCountry;
          }

          if (isAccountNumberWithQr && name === 'accountNumber') {
            rawValue = '';
            label = '';
          }

          return {
            name,
            type,
            label,
            value: rawValue,
          };
        }),
    [
      tradeMethod.fields,
      tradeMethod.paymentType,
      bank,
      countryCode,
      requisites,
      t,
      formattedCardInfo,
      requisitesBankName,
      requisitesCountry,
      isNSPK,
      isSIM,
      isAccountNumberWithQr,
      shouldShowQrCode,
    ],
  );

  const renderSberPayFields = useCallback(
    () => (
      <Button
        onClick={() =>
          window.open(requisites?.sberPayUrl, '_blank', 'noopener noreferrer')
        }
        color="success"
        className="tw-flex tw-justify-center tw-items-center tw-mb-5 tw-py-6 tw-text-xl md:tw-text-3xl !tw-text-primary"
      >
        <span className="tw-mb-1">{t('pay')}</span>
        <img
          src={`${IMAGES_PATH}sberpay-logo.svg`}
          className="tw-h-full tw-ml-2"
          alt="sberpay-logo"
        />
      </Button>
    ),
    [requisites?.sberPayUrl, t],
  );

  if (isSberPay) {
    return renderSberPayFields();
  }

  return (
    <div
      className={cx(
        'tw-flex tw-w-full tw-h-auto tw-mb-4 tw-px-3 tw-py-2 tw-rounded-xl tw-bg-layout-accent tw-border-2 tw-border-layout-accent md:tw-px-7 md:tw-py-4 md:tw-rounded-2xl',
        shouldShowQrCode ? 'tw-flex-col tw-items-stretch' : 'tw-items-center',
      )}
    >
      <div
        className={cx(
          'tw-flex tw-flex-col tw-text-md tw-font-bold tw-text-primary md:tw-text-xl',
          { 'tw-w-full tw-mb-4': shouldShowQrCode },
        )}
      >
        {formattedFields.map(renderField)}
      </div>
      <div
        className={cx(
          'tw-flex tw-flex-col tw-justify-center tw-items-center tw-self-start',
          { 'tw-w-full': shouldShowQrCode || isEmpty(formattedFields) },
        )}
      >
        {!isEmpty(formattedFields) && isCopyAvailable && (
          <Button
            style={{ top: '-1px' }}
            className={cx(
              'tw-flex tw-justify-center tw-items-center tw-self-start !tw-w-10 !tw-text-sm !tw-text-copy !tw-rounded-lg md:!tw-w-40 !tw-h-8 tw-mb-4 -tw-t-1',
            )}
            color="tertiary"
            onClick={copyCardInfo}
          >
            <SVG
              src={COPY_ICON_PATH}
              title="copy"
              className="tw-w-3.5 tw-h-3.5 md:tw-mr-3"
            />
            <div className="tw-hidden md:tw-block">{t('buttons.copy')}</div>
          </Button>
        )}
        {paymentStringQrData ? (
          <QrCode data={paymentStringQrData} className="tw-w-full md:tw-p-1" />
        ) : (
          shouldShowQrCode &&
          (qrCodeUrl ? (
            <div className="tw-w-full md:tw-p-1">
              <img
                className="tw-w-full tw-max-w-full"
                src={qrCodeUrl}
                alt={t('qr_code.placeholder')}
              />
            </div>
          ) : (
            requisites?.paymentLink && (
              <div className="tw-w-full tw-bg-white tw-border-1 tw-border-solid tw-border-gray-400 tw-rounded-xl tw-px-3 tw-pt-4 tw-pb-2 md:tw-px-4">
                <div className="tw-mb-4">
                  {renderQrPaymentLinkField({
                    name: 'paymentLink',
                    value: requisites?.paymentLink,
                    type: 'payment_link',
                    label: i18next.t(
                      'components.payment_details.payment_link.label',
                    ),
                  })}
                </div>
                {renderPaymentLinkAppButtons()}
                <QrCode
                  data={requisites?.paymentLink}
                  className={cx('tw-w-full', {
                    'tw-hidden md:tw-flex': !isEmpty(paymentLinkAppLinks),
                  })}
                  contentClassName="tw-w-full"
                  imageClassName="tw-w-full"
                  {...(requisites?.bankName && {
                    title: t('qr_code.title', {
                      bankName: requisites?.bankName,
                    }),
                  })}
                />
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
};
